// Import necessary libraries
import * as React from "react";  // React library
import { Slot } from "@radix-ui/react-slot";  // Radix UI Slot for component composition
import { cva } from "class-variance-authority";  // Utility for managing class variants

import { cn } from "@/lib/utils";  // Utility function for conditional class names

// Define button variants and sizes using cva
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",  // Default button style
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",  // Destructive action style
        outline:
          "border border-input bg-background shadow-sm hover:bg-muted hover:text-foreground",  // Outline style
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",  // Secondary button style
        ghost: "hover:bg-muted hover:text-foreground",  // Ghost button style
        link: "text-primary underline-offset-4 hover:underline",  // Link style
      },
      size: {
        default: "h-9 px-4 py-2",  // Default size
        sm: "h-8 rounded-md px-3 text-xs",  // Small size
        lg: "h-10 rounded-md px-8",  // Large size
        icon: "h-9 w-9",  // Icon button size
      },
    },
    defaultVariants: {
      variant: "default",  // Default variant
      size: "default",  // Default size
    },
  }
);

// Button component definition
import Spinner from './spinner';

const Button = React.forwardRef(({ className, variant, size, asChild = false, isLoading = false, disabled = false, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";  // Use Slot if asChild is true, otherwise use button
  const mergedDisabled = disabled || isLoading;
  
  // When using asChild, we can only have a single child element, so we cannot add the spinner
  if (asChild) {
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={mergedDisabled}
        aria-busy={isLoading}
        {...props}>
        {children}
      </Comp>
    );
  }
  
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }), isLoading ? 'opacity-90 pointer-events-none' : '')}
      ref={ref}
      disabled={mergedDisabled}
      aria-busy={isLoading}
      {...props}>
      {isLoading && (
        <span className="mr-2 flex items-center">
          <Spinner className="h-4 w-4 text-current" />
        </span>
      )}
      {children}
    </Comp>
  );
});
Button.displayName = "Button";  // Set display name for debugging

export { Button, buttonVariants };  // Export the Button component and variants
